import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { ArrowUp, MessageSquare, Bug, Lightbulb, Sparkles, MoreHorizontal, Loader2, Plus, Check, Eye, EyeOff, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEEDBACK_TYPES = [
  { value: 'bug', label: 'Bug Report', icon: Bug },
  { value: 'feature', label: 'Feature Request', icon: Sparkles },
  { value: 'new_product_idea', label: 'New Product Idea', icon: Lightbulb },
  { value: 'other', label: 'Other', icon: MessageSquare },
];

const CATEGORIES = [
  'Rubric Builder',
  'Print/Export',
  'Students',
  'Templates',
  'User Experience',
  'Mobile',
  'Integrations',
  'Other'
];

export const FeedbackPage = ({ onBack }) => {
  const { user, isAdmin } = useAuth();
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('newest');
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Admin filters
  const [adminStatusFilter, setAdminStatusFilter] = useState('');
  const [adminTypeFilter, setAdminTypeFilter] = useState('');

  // Form state
  const [formType, setFormType] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchFeedback = useCallback(async () => {
    try {
      setLoading(true);
      let url = isAdmin ? `${API}/admin/feedback` : `${API}/feedback`;
      const params = new URLSearchParams();
      
      if (!isAdmin) {
        params.append('sort_by', sortBy);
      }
      if (isAdmin && adminStatusFilter) {
        params.append('status', adminStatusFilter);
      }
      if (isAdmin && adminTypeFilter) {
        params.append('feedback_type', adminTypeFilter);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      setFeedbackList(response.data);
    } catch (err) {
      console.error('Error fetching feedback:', err);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, sortBy, adminStatusFilter, adminTypeFilter]);

  const fetchUnreadCount = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(`${API}/admin/feedback/unread-count`);
      setUnreadCount(response.data.unread_count);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchFeedback();
    fetchUnreadCount();
  }, [fetchFeedback, fetchUnreadCount]);

  const handleSubmit = async () => {
    if (!formType) {
      toast.error('Please select a feedback type');
      return;
    }
    if (!formDescription || formDescription.trim().length < 10) {
      toast.error('Please provide a description (at least 10 characters)');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        feedback_type: formType,
        category: formCategory || null,
        description: formDescription.trim()
      });
      toast.success('Thank you for your feedback!');
      setShowSubmitDialog(false);
      setFormType('');
      setFormCategory('');
      setFormDescription('');
      fetchFeedback();
    } catch (err) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpvote = async (feedbackId) => {
    try {
      const response = await axios.post(`${API}/feedback/${feedbackId}/upvote`);
      // Update local state
      setFeedbackList(prev => prev.map(f => {
        if (f.id === feedbackId) {
          const hasUpvoted = f.upvoted_by?.includes(user.id);
          return {
            ...f,
            upvotes: response.data.upvotes,
            upvoted_by: hasUpvoted 
              ? f.upvoted_by.filter(id => id !== user.id)
              : [...(f.upvoted_by || []), user.id]
          };
        }
        return f;
      }));
    } catch (err) {
      toast.error('Failed to upvote');
    }
  };

  const handleMarkRead = async (feedbackId, currentStatus) => {
    if (!isAdmin) return;
    try {
      const endpoint = currentStatus === 'unread' ? 'mark-read' : 'mark-unread';
      await axios.put(`${API}/admin/feedback/${feedbackId}/${endpoint}`);
      setFeedbackList(prev => prev.map(f => 
        f.id === feedbackId ? { ...f, status: currentStatus === 'unread' ? 'read' : 'unread' } : f
      ));
      fetchUnreadCount();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/feedback/export-csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'feedback_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Feedback exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const getTypeIcon = (type) => {
    const found = FEEDBACK_TYPES.find(t => t.value === type);
    return found ? found.icon : MessageSquare;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            Feedback & Ideas
            {isAdmin && unreadCount > 0 && (
              <span className="px-2 py-0.5 text-xs bg-primary text-primary-foreground rounded-full">
                {unreadCount} new
              </span>
            )}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Help us improve Rubric Builder
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={handleExportCSV} className="rounded-xl">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
          <Button onClick={() => setShowSubmitDialog(true)} className="rounded-xl" data-testid="submit-feedback-btn">
            <Plus className="w-4 h-4 mr-2" />
            Submit Feedback
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        {!isAdmin && (
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-40 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="most_voted">Most Voted</SelectItem>
            </SelectContent>
          </Select>
        )}
        
        {isAdmin && (
          <>
            <Select value={adminStatusFilter || "all"} onValueChange={(val) => setAdminStatusFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-40 rounded-xl">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
                <SelectItem value="read">Read</SelectItem>
              </SelectContent>
            </Select>
            <Select value={adminTypeFilter || "all"} onValueChange={(val) => setAdminTypeFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-40 rounded-xl">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {FEEDBACK_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      {/* Feedback List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : feedbackList.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-border">
          <MessageSquare className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" strokeWidth={1} />
          <h3 className="text-lg font-medium text-muted-foreground">No feedback yet</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-6">
            Be the first to share your ideas!
          </p>
          <Button onClick={() => setShowSubmitDialog(true)} className="rounded-xl">
            Submit Feedback
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {feedbackList.map((feedback) => {
            const TypeIcon = getTypeIcon(feedback.feedback_type);
            const hasUpvoted = feedback.upvoted_by?.includes(user?.id);
            
            return (
              <div
                key={feedback.id}
                className={`bg-white rounded-xl border p-4 ${
                  isAdmin && feedback.status === 'unread' ? 'border-primary/30 bg-primary/5' : 'border-border'
                }`}
                data-testid={`feedback-item-${feedback.id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Upvote Button */}
                  <button
                    onClick={() => handleUpvote(feedback.id)}
                    className={`flex flex-col items-center min-w-[50px] py-2 px-3 rounded-lg transition-colors ${
                      hasUpvoted 
                        ? 'bg-primary/10 text-primary' 
                        : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                    }`}
                    data-testid={`upvote-${feedback.id}`}
                  >
                    <ArrowUp className="w-5 h-5" />
                    <span className="text-sm font-medium">{feedback.upvotes}</span>
                  </button>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <TypeIcon className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs px-2 py-0.5 bg-muted rounded-full capitalize">
                        {feedback.feedback_type.replace('_', ' ')}
                      </span>
                      {feedback.category && (
                        <span className="text-xs text-muted-foreground">
                          • {feedback.category}
                        </span>
                      )}
                      {isAdmin && feedback.status === 'unread' && (
                        <span className="text-xs px-2 py-0.5 bg-primary text-primary-foreground rounded-full">
                          New
                        </span>
                      )}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{feedback.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      {isAdmin && (
                        <span>{feedback.user_email}</span>
                      )}
                      <span>{new Date(feedback.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Admin Actions */}
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMarkRead(feedback.id, feedback.status)}
                      className="flex-shrink-0"
                    >
                      {feedback.status === 'unread' ? (
                        <Eye className="w-4 h-4" />
                      ) : (
                        <EyeOff className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Submit Feedback</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type *</Label>
              <div className="grid grid-cols-2 gap-2">
                {FEEDBACK_TYPES.map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormType(type.value)}
                      className={`flex items-center gap-2 p-3 rounded-xl border transition-colors ${
                        formType === type.value 
                          ? 'border-primary bg-primary/5 text-primary' 
                          : 'border-border hover:bg-muted'
                      }`}
                      data-testid={`feedback-type-${type.value}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {formType === 'new_product_idea' && (
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formCategory} onValueChange={setFormCategory}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Select a category (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Tell us more... What would you like to see?"
                className="rounded-xl min-h-[120px]"
                data-testid="feedback-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-feedback-confirm">
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
